/**
 * OpenClaw SDK compatibility layer — dynamic import wrappers with caching.
 *
 * Each wrapper attempts to import a deep SDK module at runtime. If the import
 * fails (older gateway, missing SDK), the function returns a no-op result.
 * Results are cached after the first attempt.
 *
 * IMPORTANT: All imports are guarded by try/catch — the plugin degrades
 * gracefully on older OpenClaw versions.
 */

import type { AgentEventPayload, TranscriptUpdatePayload } from "./openclaw-types.js";

// ---------------------------------------------------------------------------
// requestHeartbeatNow — wake agent proactively before outbound sends
// ---------------------------------------------------------------------------

let _heartbeatFn: ((opts?: { reason?: string }) => Promise<void>) | false | null = null;

/**
 * Request an immediate heartbeat wake from the OpenClaw gateway.
 * Returns true if the heartbeat API was available and called, false otherwise.
 * Never throws.
 */
export async function requestHeartbeatNow(opts?: { reason?: string }): Promise<boolean> {
  if (_heartbeatFn === null) {
    try {
      // @ts-expect-error — deep SDK import only available when openclaw is installed
      const mod = await import("openclaw/dist/plugin-sdk/infra/heartbeat-wake.js");
      _heartbeatFn = mod.requestHeartbeatNow ?? mod.default?.requestHeartbeatNow ?? false;
    } catch {
      _heartbeatFn = false;
    }
  }
  if (typeof _heartbeatFn === "function") {
    try {
      await _heartbeatFn(opts);
      return true;
    } catch {
      return false;
    }
  }
  return false;
}

// ---------------------------------------------------------------------------
// onAgentEvent — subscribe to agent-level events for trust telemetry
// ---------------------------------------------------------------------------

type AgentEventCallback = (event: AgentEventPayload) => void;
let _agentEventFn: ((cb: AgentEventCallback) => (() => void)) | false | null = null;

/**
 * Subscribe to agent-level events (tool_use, reasoning_complete, error, etc.).
 * Returns an unsubscribe function, or a no-op if the API is unavailable.
 */
export async function onAgentEvent(callback: AgentEventCallback): Promise<() => void> {
  if (_agentEventFn === null) {
    try {
      // @ts-expect-error — deep SDK import only available when openclaw is installed
      const mod = await import("openclaw/dist/plugin-sdk/infra/agent-events.js");
      _agentEventFn = mod.onAgentEvent ?? mod.default?.onAgentEvent ?? false;
    } catch {
      _agentEventFn = false;
    }
  }
  if (typeof _agentEventFn === "function") {
    try {
      return _agentEventFn(callback);
    } catch {
      return () => {};
    }
  }
  return () => {};
}

// ---------------------------------------------------------------------------
// onSessionTranscriptUpdate — subscribe to transcript deltas
// ---------------------------------------------------------------------------

type TranscriptCallback = (update: TranscriptUpdatePayload) => void;
let _transcriptFn: ((cb: TranscriptCallback) => (() => void)) | false | null = null;

/**
 * Subscribe to session transcript updates for behavioral analysis.
 * Returns an unsubscribe function, or a no-op if the API is unavailable.
 */
export async function onSessionTranscriptUpdate(callback: TranscriptCallback): Promise<() => void> {
  if (_transcriptFn === null) {
    try {
      // @ts-expect-error — deep SDK import only available when openclaw is installed
      const mod = await import("openclaw/dist/plugin-sdk/sessions/transcript-events.js");
      _transcriptFn = mod.onSessionTranscriptUpdate ?? mod.default?.onSessionTranscriptUpdate ?? false;
    } catch {
      _transcriptFn = false;
    }
  }
  if (typeof _transcriptFn === "function") {
    try {
      return _transcriptFn(callback);
    } catch {
      return () => {};
    }
  }
  return () => {};
}
